import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  BatchGroupDetails,
  BatchWindowConfig,
  getAllBatchGroupDetails,
  getBatchWindowConfig,
} from "../api/batch";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { BatchComponent } from "../components/batch/Batch";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { PageTitle } from "../components/utils/PageTitle";
import {
  BundledDataLoader,
  DataLoaderOption,
  getAllDataLoaderSystems,
  getAvailableDataTypesAndOptions,
  getStandaloneSyncSystems,
  StandaloneSyncSystem,
} from "../api/datasystems";
import { StandaloneSyncSystemCard } from "../components/batch/StandaloneSyncSystemCard";

export function Configuration() {
  const { userProfile } = useAuth();
  const [batchWindowConfig, setBatchWindowConfig] =
    useState<BatchWindowConfig>();

  const [configuredBatchGroups, setConfiguredBatchGroups] = useState<
    BatchGroupDetails[]
  >([]);
  const [addNewItem, setAddNewItem] = useState<boolean>(false);

  const [dataLoaderOptions, setDataLoaderOptions] = useState<
    DataLoaderOption[]
  >([]);
  const [syncSystemOptions, setSyncSystemOptions] = useState<
    DataLoaderOption[]
  >([]);

  const [availableDataLoaders, setAvailableDataLoaders] = useState<
    BundledDataLoader[]
  >([]);
  const [standaloneSyncSystems, setStandaloneSyncSystems] = useState<
    StandaloneSyncSystem[]
  >([]);

  const refreshConfiguration = () => {
    getAllBatchGroupDetails()
      .then(async (batchGroupDetail) => {
        setConfiguredBatchGroups(batchGroupDetail);
      })
      .catch((err) => console.log(err));
    getAllDataLoaderSystems()
      .then((dataLoaderResponse) => {
        setAvailableDataLoaders(dataLoaderResponse);
      })
      .catch((err) => console.log(err));
    getStandaloneSyncSystems()
      .then((syncSystems) => {
        setStandaloneSyncSystems(syncSystems);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    let isMounted = true;
    refreshConfiguration();
    getBatchWindowConfig()
      .then((response) => {
        if (isMounted) {
          setBatchWindowConfig(response);
        }
      })
      .catch((err) => console.log(err));
    getAvailableDataTypesAndOptions()
      .then((dataOptionsResponse) => {
        if (isMounted) {
          setDataLoaderOptions(dataOptionsResponse.CRM || []);
          setSyncSystemOptions(dataOptionsResponse.SYNC || []);
        }
      })
      .catch((err) => console.log(err));

    return () => {
      isMounted = false;
    };
  }, []);

  const canAddNewItem = () => {
    if (addNewItem) {
      return (
        <BatchComponent
          batchWindowConfig={batchWindowConfig}
          status="New"
          dataLoaderOptions={dataLoaderOptions}
          syncSystemOptions={syncSystemOptions}
          availableDataLoaders={availableDataLoaders}
          onSaved={() => {
            setAddNewItem(false);
            refreshConfiguration();
          }}
        />
      );
    }
  };

  return (
    <div>
      <PageTitle
        pageTitle="Configuration | CIRROM"
        pageHeader={
          configuredBatchGroups.length + standaloneSyncSystems.length > 0
            ? "Configured Data Sources"
            : "Set up a new Data Source"
        }
      >
        <Container maxWidth="xl" style={{ padding: "0px" }}>
          {["CIRROM_ADMIN", "SUPER_ADMIN"].includes(userProfile) ? (
            <>
              {configuredBatchGroups.length + standaloneSyncSystems.length >
              0 ? (
                <>
                  <IconButton
                    aria-label="new"
                    color="info"
                    onClick={() => setAddNewItem(!addNewItem)}
                    size="large"
                  >
                    <AddCircleOutlineOutlinedIcon />
                  </IconButton>
                  <Container
                    maxWidth="xl"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                    }}
                  >
                    {canAddNewItem()}
                    {configuredBatchGroups.map((configuredBatchGroup) => {
                      return (
                        batchWindowConfig && (
                          <BatchComponent
                            key={`Batch-${configuredBatchGroup.id}`}
                            subheader={configuredBatchGroup.name}
                            existing={true}
                            status={configuredBatchGroup.last_batch_status}
                            id={configuredBatchGroup.id}
                            configRevisionId={
                              configuredBatchGroup.data_loader_config_revision_id
                            }
                            ruleGroupId={configuredBatchGroup.rule_group_id}
                            batchWindowConfig={batchWindowConfig}
                            dataLoaderOptions={dataLoaderOptions}
                            syncSystemOptions={syncSystemOptions}
                            availableDataLoaders={availableDataLoaders}
                            onSaved={refreshConfiguration}
                          />
                        )
                      );
                    })}
                    {standaloneSyncSystems.map((syncSystem) => {
                      return (
                        <StandaloneSyncSystemCard
                          key={`Sync-${syncSystem.id}`}
                          syncSystem={syncSystem}
                          syncSystemOptions={syncSystemOptions}
                        />
                      );
                    })}
                  </Container>
                </>
              ) : (
                <Container
                  maxWidth="xl"
                  style={{ display: "flex", flexDirection: "row" }}
                >
                  {batchWindowConfig && (
                    <BatchComponent
                      batchWindowConfig={batchWindowConfig}
                      dataLoaderOptions={dataLoaderOptions}
                      syncSystemOptions={syncSystemOptions}
                      status="New"
                      availableDataLoaders={availableDataLoaders}
                      onSaved={refreshConfiguration}
                    />
                  )}
                </Container>
              )}
            </>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  Access Restricted
                </Typography>
                <Typography variant="body1" style={{ marginTop: "10px" }}>
                  You do not have permission to view this page.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Container>
      </PageTitle>
    </div>
  );
}
